import { useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Embellishment from '../../components/Embellishment'
import Label from '../../components/Label'
import Page from '../../components/Page'
import Prompt from '../../components/Prompt'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import CircularProgress from '@mui/material/CircularProgress'
import useContentful from '../../contexts/useContentful'
import useQualtricsSurvey from '../../hooks/useQualtricsSurvey'
// import useRecaptcha from "../../hooks/useRecaptcha"
import { setUnloadRedirect } from '../../redux/slices/globalSlice'
import { find } from 'lodash'

const ThankYouPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    demographicsPageTitle,
    demographicsPageDescription,
    demographicButtonText,
    input1Label,
    input1Options,
    input2Label,
    input2Options,
    input3Label,
    input3Options,
    input4Label,
    input4Options,
    input5Label,
    input5Options,
    input6Label,
    input6Options,
    skipButtonText,
    ageRange,
    ownYourHome,
    raceEthnicity,
    ageRangeOptions,
    ownYourHomeOptions,
    raceEthnicityOptions,
    errorText,
  } = useContentful()
  const { 
    submitMultipleChoiceAnswer, 
    submitMultiSelectAnswer,
    advanceSurvey,
    // submitSurvey,
    qualtricsLoading,
  } = useQualtricsSurvey()
  const qualtricsQuestions = useSelector(({ qualtricsSurvey }) => qualtricsSurvey.qualtricsQuestions)
  const responses = useSelector(({ qualtricsSurvey }) => qualtricsSurvey.responses)
  const hasReferrerID = useSelector(({ global }) => global.hasReferrerID)
  const [isLoading, setIsLoading] = useState(false)
  const [tryAgainError, setTryAgainError] = useState(false)
  // const { executeRecaptcha } = useRecaptcha();

  const onSubmit = async setLoad => {
    if (setLoad) setIsLoading(true)
    // const response = await executeRecaptcha("demographics")
    advanceSurvey({
      // score: response?.score,
      nextIndex: 11,
      cb: error => {
        setIsLoading(false)
        if (!error) {
          setTryAgainError(false)
          if (hasReferrerID) {
            dispatch(setUnloadRedirect('https://www.research.net/r/P7CV9WZ'))
          } else {
            navigate(`/thank-you`)
          }
        } else {
          setTryAgainError(true)
        }
      },
    })
  }

  const [
    ageRangeOptionsZipped,
    raceEthnicityOptionsZipped,
    ownYourHomeOptionsZipped,
    genderOptionsZipped,
    educationOptionsZipped,
    incomeOptionsZipped,
    childrenOptionsZipped,
    politicalAffiliationOptionsZipped,
    durationOptionsZipped,
  ] = useMemo(() => {
    return [
      find(qualtricsQuestions, { questionId: 'QID59' })?.choices?.map((o, idx) => ({ ...o, display: ageRangeOptions?.[idx] })) || [],
      find(qualtricsQuestions, { questionId: 'QID61' })?.choices?.map((o, idx) => ({ ...o, display: raceEthnicityOptions?.[idx] })) || [],
      find(qualtricsQuestions, { questionId: 'QID62' })?.choices?.map((o, idx) => ({ ...o, display: ownYourHomeOptions?.[idx] })) || [],
      find(qualtricsQuestions, { questionId: 'QID71' })?.choices?.map((o, idx) => ({ ...o, display: input1Options?.[idx] })) || [],
      find(qualtricsQuestions, { questionId: 'QID72' })?.choices?.map((o, idx) => ({ ...o, display: input2Options?.[idx] })) || [],
      find(qualtricsQuestions, { questionId: 'QID73' })?.choices?.map((o, idx) => ({ ...o, display: input3Options?.[idx] })) || [],
      find(qualtricsQuestions, { questionId: 'QID74' })?.choices?.map((o, idx) => ({ ...o, display: input4Options?.[idx] })) || [],
      find(qualtricsQuestions, { questionId: 'QID76' })?.choices?.map((o, idx) => ({ ...o, display: input5Options?.[idx] })) || [],
      find(qualtricsQuestions, { questionId: 'QID77' })?.choices?.map((o, idx) => ({ ...o, display: input6Options?.[idx] })) || [],
    ]
  }, [
    ageRangeOptions,
    ownYourHomeOptions,
    raceEthnicityOptions,
    input1Options,
    input2Options,
    input3Options,
    input4Options,
    input5Options,
    input6Options,
  ])

  return (
    <Page>
      <Stack>
        <Container
          sx={{
            zIndex: 1,
          }}
        >
          <Stack
            display="grid"
            gap={4}
            py={6}
          >
            <Stack
              display="grid"
              gap={4}
              textAlign="center"
              justifySelf="center"
              justifyItems="center"
            >
              <Typography
                variant="h2"
                sx={{
                  maxWidth: 750,
                }}
              >
                {demographicsPageTitle}
              </Typography>
              {!!demographicsPageDescription && (
                <Typography
                  variant="body1"
                  color="text.neutralDark"
                  sx={{
                    maxWidth: 650,
                  }}
                >
                  {demographicsPageDescription}
                </Typography>
              )}
            </Stack>
            <Divider
              light
              sx={{
                opacity: demographicsPageDescription ? 1 : 0,
              }}
            />
            <Stack
              sx={{
                display: 'grid',
                gap: 4,
                maxWidth: '400px',
                justifySelf: 'center',
              }}
            >
              <Stack
                  gap={2}
                >
                  <Label>
                    {raceEthnicity}
                  </Label>
                  <Stack
                    gap={1.5}
                  >
                    {raceEthnicityOptionsZipped?.map(o => (
                      <FormControl 
                        key={o.display}
                        component="fieldset"
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'auto 1fr',
                          gap: 1,
                          alignItems: 'center',
                        }}
                      >
                        <Checkbox
                          checked={responses[`QID61_${o.choiceId}`]}
                          onChange={e => submitMultiSelectAnswer('QID61', o.choiceId, !!!responses?.[`${'QID61'}_${o.choiceId}`], responses)}
                        />
                        <Typography
                          variant="body1"
                        >
                          {o.display}
                        </Typography>
                      </FormControl>
                    ))}
                  </Stack>
              </Stack>
              <FormControl component="fieldset">
                <Label>
                  {ownYourHome}
                </Label>
                <RadioGroup
                  aria-label="options"
                  value={responses.QID62}
                  onChange={e => submitMultipleChoiceAnswer('QID62', e.target.value)}
                >
                  {ownYourHomeOptionsZipped?.map(o => (
                    <FormControlLabel
                      key={o.display}
                      value={o.choiceId}
                      control={<Radio />}
                      label={o.display}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              <FormControl component="fieldset">
                <Label>
                  {ageRange}
                </Label>
                <RadioGroup
                  aria-label="options"
                  value={responses.QID59}
                  onChange={e => submitMultipleChoiceAnswer('QID59', e.target.value)}
                >
                  {ageRangeOptionsZipped?.map(o => (
                    <FormControlLabel
                      key={o.display}
                      value={o.choiceId}
                      control={<Radio />}
                      label={o.display}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              <Stack
                gap={2}
              >
                <FormControl 
                  component="fieldset"
                >
                  <Label>
                    {input1Label}
                  </Label>
                  <RadioGroup
                    aria-label="options"
                    value={responses.QID71}
                    onChange={e => {
                      submitMultipleChoiceAnswer('QID71', e.target.value)
                    }}
                  >
                    {genderOptionsZipped?.map(o => (
                      <FormControlLabel
                        key={o.display}
                        value={o.choiceId}
                        control={<Radio />}
                        label={o.display}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                {responses.QID71 === '7' && (
                  <TextField
                    onChange={e => submitMultipleChoiceAnswer('QID71', '7', '7', e.target.value)}
                  />
                )}
              </Stack>
              <FormControl 
                component="fieldset"
              >
                <Label>
                  {input2Label}
                </Label>
                <RadioGroup
                  aria-label="options"
                  value={responses.QID72}
                  onChange={e => submitMultipleChoiceAnswer('QID72', e.target.value)}
                >
                  {educationOptionsZipped?.map(o => (
                    <FormControlLabel
                      key={o.display}
                      value={o.choiceId}
                      control={<Radio />}
                      label={o.display}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              <FormControl 
                component="fieldset"
              >
                <Label>
                  {input3Label}
                </Label>
                <RadioGroup
                  aria-label="options"
                  value={responses.QID73}
                  onChange={e => submitMultipleChoiceAnswer('QID73', e.target.value)}
                >
                  {incomeOptionsZipped?.map(o => (
                    <FormControlLabel
                      key={o.display}
                      value={o.choiceId}
                      control={<Radio />}
                      label={o.display}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              <Stack
                  gap={2}
                >
                  <Label>
                    {input4Label}
                  </Label>
                  <Stack
                    gap={1.5}
                  >
                    {childrenOptionsZipped?.map(o => (
                      <FormControl 
                        key={o.display}
                        component="fieldset"
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'auto 1fr',
                          gap: 1,
                          alignItems: 'center',
                        }}
                      >
                        <Checkbox
                          checked={responses[`QID74_${o.choiceId}`]}
                          onChange={e => submitMultiSelectAnswer('QID74', o.choiceId, !!!responses?.[`${'QID74'}_${o.choiceId}`], responses)}
                        />
                        <Typography
                          variant="body1"
                        >
                          {o.display}
                        </Typography>
                      </FormControl>
                    ))}
                  </Stack>
              </Stack>
              <FormControl 
                component="fieldset"
              >
                <Label>
                  {input5Label}
                </Label>
                <RadioGroup
                  aria-label="options"
                  value={responses.QID76}
                  onChange={e => submitMultipleChoiceAnswer('QID76', e.target.value)}
                >
                  {politicalAffiliationOptionsZipped?.map(o => (
                    <FormControlLabel
                      key={o.display}
                      value={o.choiceId}
                      control={<Radio />}
                      label={o.display}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              <FormControl 
                component="fieldset"
              >
                <Label>
                  {input6Label}
                </Label>
                <RadioGroup
                  aria-label="options"
                  value={responses.QID77}
                  onChange={e => submitMultipleChoiceAnswer('QID77', e.target.value)}
                >
                  {durationOptionsZipped?.map(o => (
                    <FormControlLabel
                      key={o.display}
                      value={o.choiceId}
                      control={<Radio />}
                      label={o.display}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              <Stack
                display="grid"
                gap={2.5}
                justifyItems="center"
              >
                <Button
                  sx={{
                    position: 'relative',
                  }}
                  disabled={(isLoading || qualtricsLoading)}
                  onClick={() => onSubmit(true)}
                >
                  <span
                    style={{
                      opacity: (isLoading || qualtricsLoading) ? 0 : 1,
                    }}
                  >
                    {demographicButtonText}
                  </span>
                  {(isLoading || qualtricsLoading) && (
                    <CircularProgress 
                      size={25}
                      sx={{
                        position: 'absolute',
                      }}
                    />
                  )}
                </Button>
                {tryAgainError && (
                  <Typography
                    variant="body1"
                    color="error"
                    textAlign="center"
                  >
                    {errorText}
                  </Typography>
                )}
                <Link
                  sx={{
                    cursor: 'pointer',
                  }}
                  onClick={() => onSubmit(false)}
                >
                  {skipButtonText}
                </Link>
              </Stack>
            </Stack>
          </Stack>
        </Container>
        <Embellishment />
      </Stack>
      <Prompt />
    </Page>
  )
}

export default ThankYouPage
